import { useLayoutEffect, useMemo, useRef } from 'react'
import { Marked, type Tokens } from 'marked'
import DOMPurify from 'dompurify'
import { splitPingLabel, type PingDescription } from '../agents/pingAgent'

/**
 * Message bodies are markdown, matching the existing UI (which vendors marked.min.js).
 *
 * SANITISING IS NOT OPTIONAL. Bodies are authored by other people and by agents on
 * other hosts, so rendering them as raw HTML would be stored XSS against the owner's
 * session — and that session is the thing holding the unlocked vault. marked stopped
 * shipping its own sanitiser years ago, so DOMPurify does it here.
 *
 * RAW HTML IS SHOWN, NOT RUN — and that is a bug fix, not a hardening tweak.
 *
 *   owner: Claude Clean View 有時候解析會錯誤
 *
 * marked passes raw HTML through untouched, and DOMPurify then deletes any element it
 * does not know while KEEPING that element's children. For a page of user-authored HTML
 * that is the right trade. For agent prose it is silent deletion: `<hash>`, `<session>`,
 * `Array<string>`, `<your-name>` are things Claude writes constantly, every one of them
 * looks like an unknown tag, and every one of them vanished from the screen. Measured in
 * real Chromium against the shipped component:
 *
 *   "Run `avengents check <hash>` where <hash> is the id."
 *      →  Run avengents check <hash> where  is the id.
 *   "The type is Array<string> and the file is <session>.jsonl"
 *      →  The type is Array and the file is .jsonl
 *
 * Note WHICH half survives: the one inside backticks. That is what made this "sometimes"
 * — the same word renders correctly a line above where it disappears, so the failure
 * reads as the agent having phrased it oddly rather than as the client eating it.
 *
 * Escaping raw HTML fixes it at the source and is strictly safer than sanitising it.
 * DOMPurify stays anyway: it is the layer that survives someone changing this one.
 */

/*
 * A PRIVATE instance, not the global `marked`.
 *
 * `marked.use()` mutates a module-level singleton shared with anything else that ever
 * imports marked — a renderer override installed here would silently change how some
 * other caller renders. Nothing else uses it today, which is exactly when this is cheap
 * to get right.
 */
const md = new Marked({ gfm: true, breaks: true })

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

/**
 * `<ping-agent:HASH[:label]>` — and the SAME TAG WITH NO ANGLE BRACKETS.
 *
 * Owner, 2026-09-21, with a screenshot of one in a Project Chat message: the
 * bare form is what actually arrives. It reads as a URI scheme, so marked's
 * autolinker turned `ping-agent:f60fca384841:yy-secretary` into a blue link to
 * nowhere — worse than plain text, because it looks like it does something.
 *
 * The bare form ends at whitespace or end-of-string; `[^\s<>]` keeps it from
 * swallowing a following tag. Anchored, like every tokenizer here — marked only
 * ever hands this a string that STARTS at a candidate.
 */
// `fr_…`: a friend link's address (agent sharing, phase 2) — a chip like any other, named by its label.
const PING = /^<ping-agent:(fr_[0-9a-f]{12}|[0-9a-f]+)(?::([^>]*))?>/i
const PING_BARE = /^ping-agent:(fr_[0-9a-f]{12}|[0-9a-f]+)(?::([^\s<>]*))?/i

type PingToken = Tokens.Generic & { type: 'ping'; hash: string; label: string }

/** `<chat-file:KEY>` or `<chat-file:KEY:name.md>` — a file shared into the room,
 *  referenced inline (the CLI's `--file` lane mints these). KEY's resolution
 *  source is the backend's contract (s3 key uuid or content hash — dashes
 *  allowed for the uuid form); this side renders the pill and carries the key
 *  on a data attribute for the click plumbing to claim once that contract
 *  lands. Same anchored-tokenizer discipline as PING: code spans and fences
 *  stay untouched by construction. */
const CHATFILE = /^<chat-file:([0-9a-f-]+)(?::([^>]*))?>/i

type ChatFileToken = Tokens.Generic & { type: 'chatfile'; fileKey: string; label: string }

/**
 * Render `<ping-agent:HASH:label>` as a visible chip.
 *
 * AN EXTENSION, NOT A STRING SUBSTITUTION, and that is the second half of the same bug.
 * The previous version rewrote the tag into an `<a>` before parsing, over the whole
 * body — including inside code. A message explaining how to address an agent:
 *
 *   ```
 *   <ping-agent:05e1247e5b62> hello
 *   ```
 *
 * rendered the anchor's SOURCE inside the code block: `&lt;a class="ping" href="#"…`.
 * Confirmed in real Chromium, in both fenced and inline code. Agents documenting each
 * other's addresses is not a corner case here; it is what the cards are for.
 *
 * An inline extension is consulted only where inline text is being tokenised, so code
 * spans and fences are untouched by construction rather than by a rule someone has to
 * remember. It also composes with the HTML escaping above, which the substitution could
 * not: an injected `<a>` would now be escaped and shown as source everywhere.
 */
md.use({
  extensions: [
    {
      name: 'ping',
      level: 'inline',
      // Where marked should stop consuming plain text and try this tokenizer
      // again. BOTH forms, or the bare one is reached by the autolinker first.
      start: (src: string) => src.search(/<?ping-agent:/i),
      tokenizer(src: string) {
        const m = PING.exec(src) ?? PING_BARE.exec(src)
        if (!m) return undefined
        return { type: 'ping', raw: m[0], hash: m[1] ?? '', label: m[2] ?? '' } as PingToken
      },
      renderer(token) {
        const { hash, label } = token as PingToken
        /*
         * THE CHIP SAYS WHO, NOT WHAT THE TAG LOOKED LIKE. It used to print
         * `ping-agent:<hash>:<label>` verbatim — the address, to a reader who
         * wants the name (owner, 2026-09-21). `resolve` is supplied by the
         * surface for the length of one synchronous parse; with none, the
         * label's own session is still better than the raw tag.
         */
        const d = resolve?.(hash, label)
        const text = d?.text || splitPingLabel(label).session || hash.slice(0, 8)
        // `mine` decides whether the chip offers to open anything — a chip that
        // looks tappable and cannot open a friend's session is a dead promise.
        const cls = d?.mine ? 'ping mine' : 'ping'
        const open = d?.open
        const attrs = open
          ? ` data-agent-session="${esc(open.session)}" data-agent-host="${esc(open.host ?? '')}"`
          : ''
        return `<a class="${cls}" href="#" data-agent-hash="${esc(hash)}"${attrs}>${esc(text)}</a>`
      },
    },
    {
      name: 'chatfile',
      level: 'inline',
      start: (src: string) => src.search(/<chat-file:/i),
      tokenizer(src: string) {
        const m = CHATFILE.exec(src)
        if (!m) return undefined
        return { type: 'chatfile', raw: m[0], fileKey: m[1] ?? '', label: m[2] ?? '' } as ChatFileToken
      },
      renderer(token) {
        const { fileKey, label } = token as ChatFileToken
        /*
         * A PILL, not a link — deliberately, until the key→attachment resolver
         * exists (backend contract pending): a chip that looks tappable and does
         * nothing is a promise the tap cannot keep. The label (usually the
         * filename) is the human half; the key rides data-file-key for the
         * click plumbing to claim later.
         */
        const text = label || `file:${fileKey.slice(0, 8)}`
        return `<span class="chat-file" data-file-key="${esc(fileKey)}" title="chat-file:${esc(fileKey)}">📄 ${esc(text)}</span>`
      },
    },
  ],
  renderer: {
    /*
     * Both the block and the inline HTML token arrive here. `text` is the raw source,
     * so escaping it prints what the agent actually typed.
     */
    html: ({ text }: Tokens.HTML | Tokens.Tag) => esc(text),
  },
})

/**
 * The ping resolver for the CURRENT parse.
 *
 * A module-scoped handoff rather than a marked option, because marked's
 * extension renderers take only the token. It is safe precisely because
 * `md.parse` here is SYNCHRONOUS (`async: false`): nothing else can run between
 * the assignment and the parse that reads it, so there is no interleaving to
 * get wrong. Cleared in a finally, so a throw cannot leave one surface's
 * resolver attached to the next one's render.
 */
let resolve: PingResolver | undefined

export type PingResolver = (hash: string, label: string) => PingDescription

/*
 * PARSED ONCE PER TEXT — owner, 2026-11-04: 功能變多了之後, 現在 frontend app / UI 有時候會變得很卡. Opening an agent parsed
 * every message of its window again (marked + DOMPurify, about half of the time the open took, measured on app-dev), and
 * so did every switch back to it. The HTML is a function of the text and the name resolver alone, so it is kept: per
 * resolver (a new roster is a new resolver, and its answers may differ), the most recently used PARSED_MAX texts.
 */
const PARSED_MAX = 3000
const parsedBy = new WeakMap<object, Map<string, string>>()
const NO_PING = {}
function parsedHtml(body: string, ping: PingResolver | undefined): string {
  const key = ping ?? NO_PING
  let kept = parsedBy.get(key)
  if (!kept) parsedBy.set(key, (kept = new Map()))
  const hit = kept.get(body)
  if (hit !== undefined) {
    kept.delete(body)
    kept.set(body, hit)
    return hit
  }
  const html = toHtml(body, ping)
  kept.set(body, html)
  if (kept.size > PARSED_MAX) kept.delete(kept.keys().next().value as string)
  return html
}

export function Markdown({ body, ping }: { body: string; ping?: PingResolver | undefined }) {
  const html = useMemo(() => parsedHtml(body, ping), [body, ping])
  const box = useRef<HTMLDivElement>(null)
  // Force external links to open safely — after the HTML is in, so it also covers links produced by autolinking. Once
  // per HTML, not on every render (a callback ref written inline ran this on each one).
  useLayoutEffect(() => {
    box.current?.querySelectorAll('a[href]').forEach((a) => {
      // Ping chips are in-page markers, not links to follow.
      if (a.classList.contains('ping')) return
      a.setAttribute('target', '_blank')
      a.setAttribute('rel', 'noopener noreferrer')
    })
  }, [html])

  return (
    <div
      className="md"
      // dangerouslySetInnerHTML is the point of this component; input is sanitised in toHtml
      // (fossil eslint-disable removed 2026-09-02 — that plugin was never installed)
      dangerouslySetInnerHTML={{ __html: html }}
      ref={box}
    />
  )
}

function toHtml(body: string, ping: PingResolver | undefined): string {
  resolve = ping
  let raw: string
  try {
    raw = md.parse(body, { async: false })
  } finally {
    resolve = undefined
  }
  const clean = DOMPurify.sanitize(raw, {
    // Links are the only thing that needs to leave the page, and they get
    // rel/target hardening below.
    ADD_ATTR: [
      'target', 'rel', 'data-agent-hash', 'data-file-key',
      /*
       * The jump's coordinates. LISTED FOR CONSISTENCY, NOT BECAUSE THEY WOULD
       * OTHERWISE BE DROPPED: DOMPurify's ALLOW_DATA_ATTR defaults to true, so
       * every `data-*` here already survives — measured by removing these two
       * and watching the click tests still pass. Kept so the set stays
       * explicit, and so this line does not silently become load-bearing if
       * ALLOW_DATA_ATTR is ever turned off; the sibling entries above are in
       * the same position.
       */
      'data-agent-session', 'data-agent-host',
    ],
  })
  /*
   * Every table rides inside its own horizontal scroller — owner, 2026-08-08:
   * 讓 clean view 裡 Markdown 的 table 可以在視窗寬度裡左右平移滑動 … Table 的
   * 寬度不能超過視窗寬度。水平拖動是瀏覽到更多內容的要素. Un-wrapped, a wide
   * table either squeezes its columns to slivers or stretches the message past
   * the viewport and drags the whole page sideways. The wrapper alone scrolls;
   * the table keeps natural column widths inside it (see .md-tablewrap CSS),
   * and the touch layer treats drags starting inside it as content panning.
   *
   * String-wrapped AFTER sanitising, which is safe precisely because the
   * <table> tags here are OURS: marked emits them well-formed and unnested,
   * and any table-looking text an agent typed was escaped long before this
   * line (the html renderer above prints raw HTML, never passes it through).
   */
  return clean
    .replace(/<table>/g, '<div class="md-tablewrap"><table>')
    .replace(/<\/table>/g, '</table></div>')
}
